// useShapeFilters.tsx - Updated with sorting and owner filter
import { useCallback, useMemo, useState } from 'react';
import { filterShapes } from './utils';
import { useDebounce } from '@ais-ui/custom-hooks';
import { useSettings } from '../Settings/SettingsProvider';

import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useTracksGetList } from '@ais-ui/api';
import { ShapeFilters, ShapeSortConfig } from './types';

function recursiveShapeFilter(
  shapesToBeFiltered: GeoJson[],
  shouldFilter: boolean,
  isTargetShape: (shape: GeoJson) => boolean
): GeoJson[] {
  if (!shouldFilter) {
    return shapesToBeFiltered;
  }

  // Helper function to check if a shape or any of its nested children matches the condition
  const containsTarget = (shape: GeoJson): boolean => {
    // Check if current shape matches
    if (isTargetShape(shape)) {
      return true;
    }

    // If it's a FeatureCollection, recursively check its children
    if (shape.type === 'FeatureCollection' && shape.features) {
      return shape.features.some((feature) => containsTarget(feature));
    }

    return false;
  };

  // Helper function to filter a shape and its children
  const filterShape = (shape: GeoJson): GeoJson | null => {
    // If the shape itself matches, return it as-is
    if (isTargetShape(shape)) {
      return shape;
    }

    // If it's a FeatureCollection, filter its children
    if (shape.type === 'FeatureCollection' && shape.features) {
      const filteredFeatures = shape.features
        .map((feature) => filterShape(feature))
        .filter((feature): feature is GeoJson => feature !== null);

      // Only return the collection if it has remaining children after filtering
      if (filteredFeatures.length > 0) {
        return {
          ...shape,
          features: filteredFeatures,
        };
      }
    }

    return null;
  };

  // Filter the shapes, keeping collections that contain target shapes
  return shapesToBeFiltered
    .map((shape) => {
      // If this shape or any nested shape matches, process it
      if (containsTarget(shape)) {
        return filterShape(shape);
      }
      return null;
    })
    .filter((shape): shape is GeoJson => shape !== null);
}

// Helper function to get shape type
// This properly distinguishes circles from points
function getShapeType(shape: GeoJson): string {
  if (shape.type === 'FeatureCollection') {
    return 'collection';
  }

  if (shape.type === 'Feature' && shape.geometry) {
    const geometryType = shape.geometry.type;

    // CRITICAL: Check if a Point is actually a Circle
    // A circle is a Point geometry WITH a radius property
    if (geometryType === 'Point') {
      // Check if radius property exists (circles have radius, regular points don't)
      if (
        shape.properties?.radius !== undefined &&
        shape.properties.radius !== null
      ) {
        return 'circle'; // This is a circle
      }
      return 'point'; // This is a regular point (no radius)
    }

    return shape.geometry.type.toLowerCase();
  }

  return 'unknown';
}

// Recursive sort function that sorts nested features as well
function recursiveSort(
  shapes: GeoJson[],
  sortConfig: ShapeSortConfig
): GeoJson[] {
  const compareFunction = (a: GeoJson, b: GeoJson): number => {
    let comparison = 0;

    switch (sortConfig.type) {
      case 'name':
        const nameA = a.properties?.name || '';
        const nameB = b.properties?.name || '';
        comparison = nameA.localeCompare(nameB);
        break;
      case 'type':
        const typeA = getShapeType(a);
        const typeB = getShapeType(b);

        // Define a custom sort order for shape types
        // Circles and points are now properly separated
        const typeOrder: { [key: string]: number } = {
          collection: 0,
          polygon: 1,
          circle: 2, // Circles grouped together
          linestring: 3,
          point: 4, // Points grouped together, separate from circles
          unknown: 5,
        };

        // Get the order values, defaulting to 999 for unexpected types
        const orderA = typeOrder[typeA] ?? 999;
        const orderB = typeOrder[typeB] ?? 999;

        // Compare using the defined order
        comparison = orderA - orderB;

        // Secondary sort by name if types are equal
        if (comparison === 0) {
          const nameA = a.properties?.name || '';
          const nameB = b.properties?.name || '';
          comparison = nameA.localeCompare(nameB);
        }
        break;
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  };

  // Sort the top-level shapes
  const sortedShapes = [...shapes].sort(compareFunction);

  // Recursively sort nested features
  return sortedShapes.map((shape) => {
    if (shape.type === 'FeatureCollection' && shape.features) {
      return {
        ...shape,
        features: recursiveSort(shape.features, sortConfig),
      };
    }
    return shape;
  });
}

export const useShapeFilters = (shapes: GeoJson[], search: string) => {
  const debouncedSearch = useDebounce(
    search.length >= 3 ? search : '',
    200,
    ''
  );
  const { settings } = useSettings();
  const [shapeFilters, setShapeFilters] = useState<ShapeFilters>({
    type: 'all',
    hasRoutes: false,
    favorites: false,
    owner: 'all',
  });

  const [sortConfig, setSortConfig] = useState<ShapeSortConfig>({
    type: 'name',
    direction: 'asc',
  });

  const { data: trackData } = useTracksGetList();

  const filterByFavorites = useCallback(
    (shapesToBeFiltered: GeoJson[], shouldFilter: boolean): GeoJson[] => {
      const favoriteShapesIds =
        settings['ribbonPlugin.settings']?.favorite_shapes?.shapes ?? [];

      return recursiveShapeFilter(
        shapesToBeFiltered,
        shouldFilter,
        (shape) =>
          shape.properties?.id !== undefined &&
          favoriteShapesIds.includes(shape.properties.id)
      );
    },
    [settings]
  );

  const filterByRoutes = useCallback(
    (shapesToBeFiltered: GeoJson[], shouldFilter: boolean): GeoJson[] => {
      const trackShapeIds = (trackData?.records ?? [])
        .map((track) => track.destination?.location?.id)
        .filter((id): id is string => id !== undefined);

      return recursiveShapeFilter(
        shapesToBeFiltered,
        shouldFilter,
        (shape) =>
          shape.properties?.id !== undefined &&
          trackShapeIds.includes(shape.properties.id)
      );
    },
    [trackData]
  );

  const filterByOwner = useCallback(
    (shapesToBeFiltered: GeoJson[], ownerFilter: string): GeoJson[] => {
      if (ownerFilter === 'all') {
        return shapesToBeFiltered;
      }

      return recursiveShapeFilter(
        shapesToBeFiltered,
        true,
        (shape) => shape.properties?.owner === ownerFilter
      );
    },
    []
  );

  // Function to find parent collections that should be expanded - used as an action, not state
  const findParentCollectionsToExpand = useCallback(
    (filteredShapes: GeoJson[], searchTerm: string): Set<string> => {
      const parentsToOpen = new Set<string>();

      // Early return if search is too short - this is critical for performance
      if (searchTerm.length < 3) {
        return parentsToOpen;
      }

      const searchLower = searchTerm.toLowerCase();

      const checkShapeAndChildren = (
        shape: GeoJson,
        parentId?: string
      ): boolean => {
        let hasMatch = false;

        // Check if current shape matches search (and is in the filtered results)
        if (shape.properties?.name?.toLowerCase().includes(searchLower)) {
          hasMatch = true;
          if (parentId) {
            parentsToOpen.add(parentId);
          }
        }

        // If this is a feature collection, check its children
        if (shape.type === 'FeatureCollection' && shape.features) {
          const currentId = shape.properties?.id;
          let childrenHaveMatch = false;

          for (const feature of shape.features) {
            if (checkShapeAndChildren(feature, currentId)) {
              childrenHaveMatch = true;
            }
          }

          if (childrenHaveMatch) {
            hasMatch = true;
            if (currentId) {
              parentsToOpen.add(currentId);
            }
            if (parentId) {
              parentsToOpen.add(parentId);
            }
          }
        }

        return hasMatch;
      };

      // Only process if we have a valid search term
      if (searchTerm.length >= 3) {
        filteredShapes.forEach((shape) => {
          checkShapeAndChildren(shape);
        });
      }

      return parentsToOpen;
    },
    []
  );

  const filteredShapes = useMemo(() => {
    let allowedGeometryTypes: string[] = [];
    if (shapeFilters.type === 'all') {
      allowedGeometryTypes = ['LineString', 'Point', 'Polygon', 'Circle'];
    } else if (shapeFilters.type === 'polygon') {
      allowedGeometryTypes = ['Polygon'];
    } else if (shapeFilters.type === 'circle') {
      allowedGeometryTypes = ['Circle'];
    } else if (shapeFilters.type === 'linestring') {
      allowedGeometryTypes = ['LineString'];
    } else if (shapeFilters.type === 'point') {
      allowedGeometryTypes = ['Point'];
    }

    const shapesFilteredBySearchAndType = filterShapes(
      shapes,
      debouncedSearch,
      allowedGeometryTypes,
      true
    );

    const shapesFilteredByOwner = filterByOwner(
      shapesFilteredBySearchAndType,
      shapeFilters.owner
    );

    const shapesFilteredByFavorites = filterByFavorites(
      shapesFilteredByOwner,
      shapeFilters.favorites
    );

    const shapesFilteredByRoutes = filterByRoutes(
      shapesFilteredByFavorites,
      shapeFilters.hasRoutes
    );

    // Apply sorting as the final step
    return recursiveSort(shapesFilteredByRoutes, sortConfig);
  }, [
    debouncedSearch,
    filterByFavorites,
    filterByRoutes,
    filterByOwner,
    shapeFilters.favorites,
    shapeFilters.hasRoutes,
    shapeFilters.type,
    shapeFilters.owner,
    shapes,
    sortConfig,
  ]);

  return {
    filteredShapes,
    shapeFilters,
    setShapeFilters,
    sortConfig,
    setSortConfig,
    // Export the function so the component can call it as an action
    findParentCollectionsToExpand,
  };
};
